import React, { useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Chip, Tooltip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, InputAdornment, Select, MenuItem, FormControl,
  InputLabel, Checkbox, ListItemText, OutlinedInput
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloudIcon from '@mui/icons-material/Cloud';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';

function exportCsv(rows) {
  const headers = ['Instance', 'Domain', 'Chunk Upload', 'Workflow Broker', 'FS Sync', 'Region', 'Release Version'];
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [
    headers.join(','),
    ...rows.map((r) => [
      escape(r['Friendly name'] || r['Instance Name']),
      escape(r['Instance Domain']),
      escape(r.chunkUploadEnabled === true ? 'Enabled' : r.chunkUploadEnabled === false ? 'Disabled' : 'NR'),
      escape(r.workflowBroker || 'NR'),
      escape(r.dbRepoSyncJob || 'NR'),
      escape(r['Region'] || 'NR'),
      escape(r['Release'] || 'NR'),
    ].join(','))
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `azure-config-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const SummaryCard = ({ label, value, total, color, icon }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
    <CardContent sx={{ pb: '12px !important' }}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <Box sx={{ color, fontSize: 22 }}>{icon}</Box>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>{label}</Typography>
      </Box>
      <Typography variant="h4" fontWeight={700} color={color}>{value}</Typography>
      {total != null && (
        <Typography variant="caption" color="text.secondary">of {total} total</Typography>
      )}
    </CardContent>
  </Card>
);

const boolChip = (val) => {
  if (val === true)  return <Chip icon={<CheckCircleIcon />} label="Enabled"  size="small" color="success" variant="outlined" />;
  if (val === false) return <Chip icon={<CancelIcon />}      label="Disabled" size="small" color="error"   variant="outlined" />;
  return <Chip icon={<HelpOutlineIcon />} label="NR" size="small" variant="outlined" sx={{ color: 'text.disabled' }} />;
};

const strCell = (val) => (
  val
    ? <Typography variant="body2" fontSize="0.8rem">{val}</Typography>
    : <Typography variant="body2" color="text.disabled" fontSize="0.8rem">NR</Typography>
);

const CHUNK_OPTIONS = [
  { value: 'enabled',  label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'nr',       label: 'Not fetched' },
];

const MultiSelect = ({ label, value, onChange, options }) => (
  <FormControl size="small" sx={{ minWidth: 160 }}>
    <InputLabel>{label}</InputLabel>
    <Select
      multiple
      value={value}
      onChange={(e) => onChange(e.target.value)}
      input={<OutlinedInput label={label} />}
      renderValue={(selected) =>
        selected.length === 0 ? 'All' : selected.length === 1 ? selected[0] : `${selected.length} selected`
      }
    >
      {options.map((opt) => {
        const v = typeof opt === 'object' ? opt.value : opt;
        const l = typeof opt === 'object' ? opt.label : opt;
        return (
          <MenuItem key={v} value={v} dense>
            <Checkbox checked={value.includes(v)} size="small" />
            <ListItemText primary={l} primaryTypographyProps={{ fontSize: '0.85rem' }} />
          </MenuItem>
        );
      })}
    </Select>
  </FormControl>
);

const AzureConfigTab = ({ customerInstances = [] }) => {
  const rows = useMemo(() => customerInstances, [customerInstances]);

  const [search, setSearch]           = useState('');
  const [filterChunk, setFilterChunk] = useState([]);
  const [filterBroker, setFilterBroker] = useState([]);
  const [filterFsSync, setFilterFsSync] = useState([]);
  const [filterRegion, setFilterRegion] = useState([]);

  const brokerOptions = useMemo(() => Array.from(new Set(rows.map((r) => r.workflowBroker).filter(Boolean))).sort(), [rows]);
  const fsSyncOptions = useMemo(() => Array.from(new Set(rows.map((r) => r.dbRepoSyncJob).filter(Boolean))).sort(), [rows]);
  const regionOptions = useMemo(() => Array.from(new Set(rows.map((r) => r['Region']).filter(Boolean))).sort(), [rows]);

  const totalCount   = rows.length;
  const chunkEnabled = rows.filter((r) => r.chunkUploadEnabled === true).length;
  const dbSyncDis    = rows.filter((r) => r.dbRepoSyncJob && r.dbRepoSyncJob.toLowerCase() === 'disabled').length;
  const asbCount     = rows.filter((r) => r.workflowBroker === 'ASB').length;

  const filtered = useMemo(() => {
    let list = rows;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        (r['Friendly name'] || r['Instance Name'] || '').toLowerCase().includes(q) ||
        (r['Instance Domain'] || '').toLowerCase().includes(q)
      );
    }
    if (filterChunk.length > 0) {
      list = list.filter((r) => {
        const v = r.chunkUploadEnabled === true ? 'enabled' : r.chunkUploadEnabled === false ? 'disabled' : 'nr';
        return filterChunk.includes(v);
      });
    }
    if (filterBroker.length > 0) list = list.filter((r) => filterBroker.includes(r.workflowBroker || 'NR'));
    if (filterFsSync.length > 0) list = list.filter((r) => filterFsSync.includes(r.dbRepoSyncJob || 'NR'));
    if (filterRegion.length > 0) list = list.filter((r) => filterRegion.includes(r['Region'] || 'NR'));
    return list;
  }, [rows, search, filterChunk, filterBroker, filterFsSync, filterRegion]);

  const activeFilters = [filterChunk, filterBroker, filterFsSync, filterRegion].filter((f) => f.length > 0).length;
  const clearFilters = () => { setFilterChunk([]); setFilterBroker([]); setFilterFsSync([]); setFilterRegion([]); setSearch(''); };

  return (
    <Box>
      {/* Summary cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <SummaryCard label="Total Clients" value={totalCount} color="#1565C0" icon={<CloudIcon fontSize="inherit" />} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard label="Chunk Upload Enabled" value={chunkEnabled} total={totalCount} color="#2e7d32" icon={<CheckCircleIcon fontSize="inherit" />} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard label="FS Sync Disabled" value={dbSyncDis} total={totalCount} color="#c62828" icon={<CancelIcon fontSize="inherit" />} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard label="Workflow Broker: ASB" value={asbCount} total={totalCount} color="#6a1b9a" icon={<CloudIcon fontSize="inherit" />} />
        </Grid>
      </Grid>

      {/* Filters */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
        <TextField
          size="small"
          placeholder="Search instance or domain…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <MultiSelect label="Chunk Upload" value={filterChunk} onChange={setFilterChunk} options={CHUNK_OPTIONS} />
        <MultiSelect
          label="Workflow Broker"
          value={filterBroker}
          onChange={setFilterBroker}
          options={[...brokerOptions, 'NR']}
        />
        <MultiSelect
          label="FS Sync"
          value={filterFsSync}
          onChange={setFilterFsSync}
          options={[...fsSyncOptions, 'NR']}
        />
        <MultiSelect
          label="Region"
          value={filterRegion}
          onChange={setFilterRegion}
          options={[...regionOptions, 'NR']}
        />
        {activeFilters > 0 && (
          <Button size="small" variant="outlined" onClick={clearFilters}>
            Clear filters ({activeFilters})
          </Button>
        )}
        <Box flexGrow={1} />
        <Typography variant="body2" color="text.secondary" alignSelf="center">
          {filtered.length} of {totalCount} clients
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() => exportCsv(filtered)}
          sx={{ textTransform: 'none' }}
        >
          Export CSV
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 520 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f5f5', fontSize: '0.8rem', whiteSpace: 'nowrap' } }}>
              <TableCell>#</TableCell>
              <TableCell>Instance</TableCell>
              <TableCell>Domain</TableCell>
              <TableCell align="center">Chunk Upload</TableCell>
              <TableCell align="center">Workflow Broker</TableCell>
              <TableCell align="center">FS Sync</TableCell>
              <TableCell>Region</TableCell>
              <TableCell>Release Version</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No clients match the current filters.
                </TableCell>
              </TableRow>
            ) : filtered.map((row, idx) => (
              <TableRow key={row._id || row['Instance Name']} hover>
                <TableCell sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>{idx + 1}</TableCell>
                <TableCell sx={{ fontWeight: 500, fontSize: '0.82rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Tooltip title={row['Instance Name']} placement="top">
                    <span>{row['Friendly name'] || row['Instance Name']}</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row['Instance Domain'] || '—'}
                </TableCell>
                <TableCell align="center">{boolChip(row.chunkUploadEnabled ?? null)}</TableCell>
                <TableCell align="center">{strCell(row.workflowBroker)}</TableCell>
                <TableCell align="center">{strCell(row.dbRepoSyncJob)}</TableCell>
                <TableCell sx={{ fontSize: '0.78rem' }}>{strCell(row['Region'])}</TableCell>
                <TableCell sx={{ fontSize: '0.78rem' }}>{strCell(row['Release'])}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AzureConfigTab;
